import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress,
  Chip,
} from '@mui/material';
import api from '../../utils/api';
import { ListPageLayout } from '../../components/ListPage';
import { tokens } from '../../theme/tokens';

export default function CommunicationPurchaseHistory() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPurchases();
  }, [page, rowsPerPage]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (page === 0) {
        fetchPurchases();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [search]);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/communication/purchase-history', {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search: search || undefined,
        },
      });
      setPurchases(response.data.data || []);
      setTotal(Number(response.data.meta?.total) || 0);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch purchase history');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <ListPageLayout
      title="Purchase History"
      crumbs={
        <>
          Operations / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Purchase History</Box>
        </>
      }
      searchPlaceholder="Search purchase history..."
      searchValue={search}
      onSearchChange={setSearch}
      error={error}
      onErrorClose={() => setError('')}
    >
      <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>SMS Credits</TableCell>
                  <TableCell>Payment Method</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchases.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                        No records yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  purchases.map((purchase) => (
                    <TableRow key={purchase.id} hover>
                      <TableCell>{formatDate(purchase.created_at)}</TableCell>
                      <TableCell>{formatCurrency(purchase.amount)}</TableCell>
                      <TableCell>{purchase.sms_credits}</TableCell>
                      <TableCell>{purchase.payment_method || '-'}</TableCell>
                      <TableCell>{purchase.payment_reference || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={purchase.status || 'Completed'}
                          color={purchase.status === 'completed' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </TableContainer>
    </ListPageLayout>
  );
}
